import React, { useState, useEffect } from 'react';
import BiddingLogin from './BiddingLogin';
import BiddingDashboard from './BiddingDashboard';
import { checkSession } from '../../../services/externalBiddingService';

/**
 * External Bidding Portal
 * Main entry point for employee bidding system
 * 
 * Flow:
 * 1. Employee enters employee code
 * 2. Receives OTP via SMS
 * 3. Verifies OTP to authenticate
 * 4. Access bidding dashboard
 */
const ExternalBidding = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on load
  useEffect(() => {
    const checkExistingSession = async () => {
      const storedSession = localStorage.getItem('bidding_session');
      
      if (storedSession) {
        try {
          const parsedSession = JSON.parse(storedSession);
          
          // Verify session is still valid with API
          try {
            const response = await checkSession(parsedSession.session_token);
            
            if (response.status === 1) {
              setSession(parsedSession);
            } else {
              // Session explicitly expired on server, clear storage
              localStorage.removeItem('bidding_session');
            }
          } catch (apiError) {
            // API call failed - could be network issue
            // Check if session token looks valid (not expired locally)
            if (parsedSession.session_token && parsedSession.employee) {
              // Trust the local session, let actual operations validate
              console.log('Session check API failed, using cached session');
              setSession(parsedSession);
            } else {
              localStorage.removeItem('bidding_session');
            }
          }
        } catch (parseError) {
          console.error('Session parse failed:', parseError);
          localStorage.removeItem('bidding_session');
        }
      }
      
      setLoading(false);
    };

    checkExistingSession();
  }, []);

  // Handle successful authentication
  const handleAuthenticated = (sessionData) => {
    // Save session to localStorage for persistence
    localStorage.setItem('bidding_session', JSON.stringify(sessionData));
    setSession(sessionData);
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('bidding_session');
    setSession(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Render login or dashboard based on session
  return session ? (
    <BiddingDashboard session={session} onLogout={handleLogout} />
  ) : (
    <BiddingLogin onLoginSuccess={handleAuthenticated} />
  );
};

export default ExternalBidding;
